<template>
    <form class="cyt-availability-notification" @submit.prevent="submit" v-if="isVisible">
        <slot name="before"></slot>

        <div class="input input-unit" data-validate="mail">
            <label :for="'mail_' + _uid">{{ $translate("cytItemAvailabilityNotification::Template.mailInputLabel") }}</label>
            <input type="email" autocomplete="email" :id="'mail_' + _uid" v-model="mailValue" :disabled="isLoading">
        </div>

        <div class="form-check small mb-3" v-if="hasPrivacyHint && requirePrivacyCheck">
            <input class="form-check-input"
                   v-if="requirePrivacyCheck"
                   type="checkbox"
                   v-model="privacyAccepted"
                   :id="'cyt_privacy_check_' + _uid">

            <label class="form-check-label" :for="'cyt_privacy_check_' + _uid">
                <slot name="privacyhint"></slot>
            </label>
        </div>

        <div class="privacy-hint small mb-3" v-if="hasPrivacyHint && !requirePrivacyCheck">
            <slot name="privacyhint"></slot>
        </div>

        <button type="submit" class="btn btn-primary btn-appearance" :disabled="submitDisabled">{{ $translate("cytItemAvailabilityNotification::Template.submitLabel") }}</button>

        <slot name="after"></slot>
    </form>
</template>

<script>
export default {
    data() {
        return {
            mailValue: "",
            isLoading: false,
            privacyAccepted: false
        }
    },

    inject: {
        itemId: {
            default: null
        }
    },

    props: {
        mail: {
            type: String,
            default: "",
            required: false
        },

        visibility: {
            type: Array,
            default: ['availability', 'gte', 5, false]
        },

        comparison: {
            type: Array,
            default: ['availability', 'lt', 5]
        },

        requirePrivacyCheck: {
            type: Boolean,
            default: false,
            required: false
        }
    },

    mounted()
    {
        this.mailValue = this.mail || "";
        this.$store.watch(
            (state) => {
                return state.user.userData;
            },
            (userData) => {
                if(!this.mailValue && userData && userData.email) {
                    this.mailValue = userData.email;
                }
            }
        )
    },

    computed:
    {
        variation() {
            const currentVariation = this.$store.getters[`${this.itemId}/currentItemVariation`];
            return currentVariation && currentVariation.variation ? currentVariation : null;
        },

        availability() {
            const currentVariation = this.variation;
            return !!currentVariation
                ? currentVariation.variation.availability.id
                : 0;
        },

        stock() {
            const currentVariation = this.variation;
            return (!!currentVariation && !!currentVariation.stock)
                ? currentVariation.stock.net
                : 0;
        },

        submitDisabled()
        {
            return this.isLoading || (this.requirePrivacyCheck && this.hasPrivacyHint && !this.privacyAccepted);
        },

        hasPrivacyHint()
        {
            return !!this.$slots.privacyhint;
        },

        isVisible()
        {
            if (App.isShopBuilder)
            {
                return true;
            }

            const variationCount = this.$store.state.items[this.itemId]?.variationSelect.variations.length;
            if(this.visibility[3] // component is configured to not be displayed for main variations
                && variationCount > 1 // and current item has different variations (the main variation itself is also take in count)
                && (!this.variation.variation.mainVariationId || this.variation.variation.mainVariationId === this.variation.variation.id) // and current variation is the main variation
            ) {
                // do not display the form for main variation if configured
                return false;
            }

            const stockLimitation = parseInt(this.variation.variation.stockLimitation)
            if(this.visibility[0] === 'stock' && (stockLimitation === 0 || stockLimitation === 2))
            {
                // do not display form if variation is not limited on stock.
                return false;
            }

            const currentValue = this.getCurrentValueForCondition(this.visibility);
            const expectedValue = this.getComparisonValue(this.visibility);
            switch (this.visibility[1])
            {
                case 'lte':
                    return currentValue <= expectedValue;
                case 'lt':
                    return currentValue < expectedValue;
                case 'gte':
                    return currentValue >= expectedValue;
                case 'gt':
                    return currentValue > expectedValue;
                case 'eq':
                default:
                    return currentValue === expectedValue;
            }
        }
    },

    methods:
    {
        isValid()
        {
            const mailRegEx = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
            return mailRegEx.test(this.mailValue);
        },

        getCurrentValueForCondition(condition)
        {
            if(condition[0] === 'availability')
            {
                return this.availability;
            }
            else
            {
                return this.stock;
            }
        },

        getComparisonValue(condition)
        {
            return condition[2] === null ? this.getCurrentValueForCondition(condition) : condition[2];
        },

        submit()
        {
            if(this.submitDisabled)
            {
                return;
            }

            if(!!this.variation && this.isValid() && !this.isLoading)
            {
                this.isLoading = true;
                $.ajax(
                    "/rest/cyt/item_availability",
                    {
                        method:     "POST",
                        dataType:   "json",
                        data: {
                            variationId: this.variation.variation.id,
                            mail: this.mailValue,
                            comparisonField: this.comparison[0],
                            comparisonType: this.comparison[1],
                            comparisonValue: this.getComparisonValue(this.comparison)
                        }
                    })
                    .done((response) =>
                    {
                        this.isLoading = false;
                        window.CeresNotification && window.CeresNotification.success(
                            this.$translate("cytItemAvailabilityNotification::Template.subscriptionSucceed")
                        );
                    })
                    .fail((err) =>
                    {
                        this.isLoading = false;
                        window.CeresNotification && window.CeresNotification.error(
                            this.$translate("cytItemAvailabilityNotification::Template.subscriptionFailed")
                        );
                        console.error(err);
                    });
            }
            else
            {
                window.CeresNotification && window.CeresNotification.error(
                    this.$translate("cytItemAvailabilityNotification::Template.invalidMail")
                );
            }
        },

    }
}
</script>
